import { useTranslator } from "@eo-locale/react";

import { ChangeEvent, useEffect } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useRouter } from "next/router";

import { CommonFileQuestion } from "@/storybook-components";
import { questionNumber, questionTitleWithNumber } from "@/helpers/survey";
import {
  getPrivateFileDownloadUrl,
  getPresignedFileUrl
} from "@/helpers/private-file-url";
import { appendAuthTokenToSurveyFileUrl } from "@/lib";
import { privateBucketFileUpload, fileUpload } from "@/models";
import useUser from "@/hooks/useUser";

import {
  setSurveyAnswers,
  setSurveyErrors,
  removeSurveyAnswers
} from "@/redux/features/survey/slice";
import { verifyRecaptchaV3 } from "@/redux/features/survey/thunk";
import type { Question } from "@/types";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import useEmbeds from "@/hooks/useEmbeds";
import googleTranslateInit from "@/helpers/google-translate-init";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";
import { useIsPrivateSurveyMediaBucketEnabled } from "@/hooks/toggle";
import { spacing, textStyles } from "./question-styles";
import { surveyParticipantFieldControl } from "./survey-participant-test-ids";

interface IFileQuestion {
  question: Question;
  participantFieldId: string;
}

const MAX_FILE_SIZE = 200000000;

const FileQuestion = ({
  question,
  participantFieldId
}: IFileQuestion): JSX.Element => {
  const { translate } = useTranslator();
  const { query } = useRouter();
  const dispatch = useAppDispatch();
  const { isEmbed } = useEmbeds();
  const user = useAppSelector((state) => state.user);
  const anonymousUser = useAppSelector((state) => state.anonymousUser);
  const { token } = fetchAuthToken({ user, anonymousUser });

  const survey = useAppSelector((state) => state.survey);
  const { answers, errors, saving } = survey;
  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);
  const { settings } = useAppSelector((state) => state.settings);
  const { googleTranslationEnabled, recaptchaSignupEnabled } = settings;
  const { isPreviewUser } = useUser();
  const isPrivateBucketEnabled = useIsPrivateSurveyMediaBucketEnabled();

  const error = errors[question.id] ?? {};
  const answer = answers[question.id] ?? {};

  // When enable_private_bucket_for_survey is enabled, the backend returns
  // /survey_files/{id}/download URLs. Anonymous / unverified participants
  // cannot send the `x-api-token` header via an `<img>` request, so append it
  // to the private download URL as a query param purely for client-side display.
  // Logged-in users authenticate via same-origin session cookies, so we skip
  // the token append for them to avoid leaking the registered-user token into
  // the DOM / referrer. Public bucket URLs are passed through unchanged.
  const isAnonymousParticipant = !user?.registeredUser?.isLoggedIn;
  const displayAnswer = answer?.value
    ? {
        ...answer,
        value:
          !getPresignedFileUrl(answer) && isAnonymousParticipant
            ? appendAuthTokenToSurveyFileUrl(answer.value, token)
            : answer.value
      }
    : answer;

  const isRecaptchaEnabled =
    recaptchaSignupEnabled && !!process.env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY;

  // reCAPTCHA v3 setup
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    if (isPreviewUser) {
      dispatch(
        setSurveyAnswers({
          questionId: question.id,
          answer: {
            value: "placeholder", // cannot be empty
            file_name: "example-document.pdf",
            file_size: 0,
            file_content_type: "application/pdf"
          }
        })
      );
    }
  }, []);

  const handleChange = async (
    e: ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (selectedFile.size > MAX_FILE_SIZE) {
      dispatch(
        setSurveyErrors({
          questionId: question.id,
          error: { code: "tooBigFile" }
        })
      );
      googleTranslateInit(isEmbed, googleTranslationEnabled, { force: true });
      return;
    }

    try {
      // Execute reCAPTCHA v3 if enabled
      if (isRecaptchaEnabled && executeRecaptcha) {
        try {
          const recaptchaV3Token = await executeRecaptcha("file_upload");

          if (recaptchaV3Token) {
            // Verify token with backend (which calls Google API)
            await dispatch(
              verifyRecaptchaV3({
                recaptcha_token: recaptchaV3Token,
                action: "file_upload"
              })
            );
          }
        } catch (recaptchaError) {
          // Log reCAPTCHA errors but don't block the file upload
          console.warn("reCAPTCHA v3 error:", recaptchaError);
        }
      }

      const projectId = query.projectId as string;
      const surveyToolId = query.surveyId as string;

      let file;
      if (isPrivateBucketEnabled) {
        // New private bucket flow: /api/v2/survey_files
        if (!projectId || !surveyToolId) {
          throw new Error(
            "Missing project or survey tool context for file upload"
          );
        }
        file = await privateBucketFileUpload(
          token,
          baseUrl,
          selectedFile,
          projectId,
          surveyToolId
        );
      } else {
        // Old public bucket flow: /api/v2/file_uploads
        // Note: recaptcha v3 is verified separately via verifyRecaptchaV3 thunk
        file = await fileUpload(token, baseUrl, selectedFile, null, null);
      }
      dispatch(
        setSurveyAnswers({
          questionId: question.id,
          answer: {
            value: getPrivateFileDownloadUrl(file, "survey") ?? file.url,
            presigned_file_url: getPresignedFileUrl(file),
            file_name: selectedFile.name,
            file_size: selectedFile.size,
            file_content_type: selectedFile.type
          }
        })
      );
      // initiate google translate when adding a file to translate the delete, replace buttons
      googleTranslateInit(isEmbed, googleTranslationEnabled, { force: true });
      // eslint-disable-next-line no-unused-vars
    } catch (error) {
      dispatch(
        setSurveyErrors({
          questionId: question.id,
          error: { code: "uploadError" }
        })
      );
      // Re-translate the error text and the restored upload button label.
      googleTranslateInit(isEmbed, googleTranslationEnabled, { force: true });
    }
  };

  const removeImage = () => {
    // initiate google translate when removing the image to translate placeholders
    googleTranslateInit(isEmbed, googleTranslationEnabled, { force: true });
    dispatch(
      removeSurveyAnswers({
        questionId: question.id
      })
    );
  };

  const fileTids = {
    uploadButton: surveyParticipantFieldControl(
      participantFieldId,
      "file-upload-button"
    ),
    fileInput: surveyParticipantFieldControl(participantFieldId, "file-input"),
    replaceButton: surveyParticipantFieldControl(
      participantFieldId,
      "file-replace-button"
    ),
    deleteButton: surveyParticipantFieldControl(
      participantFieldId,
      "file-delete-button"
    ),
    previewImage: surveyParticipantFieldControl(
      participantFieldId,
      "file-preview-image"
    )
  };

  return (
    <CommonFileQuestion
      layoutProps={{ width: "100%" }}
      invalid={!!error.code}
      required={question.required}
      className={error.code ? "ehq-question-error" : null}
      gap={spacing.question}
      questionLabel={questionTitleWithNumber(
        questionNumber(question, survey),
        question
      )}
      questionLabelProps={{ ...textStyles?.label }}
      labelClassName="ehq-surveyQuestionHeading"
      accessibilityTextOfRequired={`* ${translate("common.required")}`}
      answer={displayAnswer}
      answerAriaLabel={
        answer?.file_name
          ? translate("common.changeFile")
          : translate("common.chooseFile")
      }
      questionId={question?.id}
      disabled={saving}
      handleFileUpload={handleChange}
      loadingText={`${translate("common.uploading")}...`}
      deleteText={translate("common.delete")}
      replaceText={translate("common.replace")}
      onDelete={removeImage}
      deleteDisabled={isPreviewUser}
      replaceDisabled={isPreviewUser}
      contentClassName="ehq-surveyFileInputFeild"
      size={{ base: "lg", lg: "md" }}
      answerProps={{ ...textStyles?.content }}
      footerStackSpacing={spacing.footerStack}
      note={question.notes}
      noteProps={{ ...textStyles?.footer }}
      errorMessage={error?.code && translate(`errors.${error.code}`)}
      errorMessageProps={{ ...textStyles?.error }}
      uploadButtonText={translate("questions.fileUpload.uploadFile")}
      fileUploadDataTestIds={fileTids}
      isPrivateBucketEnabled={isPrivateBucketEnabled}
    />
  );
};

export default FileQuestion;
